import { Skeleton } from "@/components/ui/skeleton";
import { DateRange, RelativeTime, TagList } from "@/components/data";
import { cargoAtualTexto, experienciaTexto } from "@/components/freelancers/contato/contatoUtils";
import { toneTextClasses } from "@/lib/tones";
import { cn } from "@/lib/utils";
import type { LinkedInPainel } from "@/types/freelancer-painel";
import { linkedinVazio } from "./linkedinPerfil";

/** O que a plataforma leu do LinkedIn: no painel, resumido; na ficha, completo. */
export function LinkedInResumo({
  linkedin,
  isLoading,
  completo = false,
}: {
  linkedin: LinkedInPainel | undefined;
  isLoading: boolean;
  completo?: boolean;
}) {
  if (isLoading) return <Skeleton className="h-20 w-full" />;
  if (!linkedin || linkedinVazio(linkedin)) {
    return (
      <p className="text-sm text-muted-foreground">
        {linkedin?.url_status === "ok" ? "LinkedIn ainda não lido pela plataforma." : "Sem LinkedIn válido cadastrado."}
      </p>
    );
  }
  const cargo = cargoAtualTexto(linkedin);
  const experiencia = experienciaTexto(linkedin);
  const experiencias = completo ? linkedin.experiencias : linkedin.experiencias.slice(0, 3);
  return (
    <div className="space-y-2 text-sm" data-testid="linkedin-resumo">
      {linkedin.headline && <p className="font-medium">{linkedin.headline}</p>}
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
        {cargo && <span>{cargo}</span>}
        {experiencia && <span>{experiencia}</span>}
        {linkedin.aberto_a_oportunidades && <span className={toneTextClasses.success}>Aberto a oportunidades</span>}
        {linkedin.atualizado_em && (
          <span>
            Lido <RelativeTime value={linkedin.atualizado_em} />
          </span>
        )}
      </div>
      {linkedin.resumo && (
        <p className={cn("whitespace-pre-line text-sm text-muted-foreground", !completo && "line-clamp-4")}>{linkedin.resumo}</p>
      )}
      {experiencias.length > 0 && (
        <ul className="space-y-1">
          {experiencias.map((experienciaItem, index) => (
            <li key={`${experienciaItem.empresa}-${experienciaItem.inicio}-${index}`} className="flex items-baseline justify-between gap-2 text-xs">
              <span className="min-w-0 truncate">
                <span className="font-medium">{experienciaItem.cargo ?? "Cargo não informado"}</span>
                {experienciaItem.empresa && <span className="text-muted-foreground"> · {experienciaItem.empresa}</span>}
              </span>
              <span className="shrink-0 text-muted-foreground">
                {experienciaItem.atual ? "atual" : <DateRange start={experienciaItem.inicio} end={experienciaItem.fim} />}
              </span>
            </li>
          ))}
        </ul>
      )}
      {linkedin.skills_principais.length > 0 && (
        <TagList items={linkedin.skills_principais} variant="skill" max={completo ? 20 : 8} size="xs" />
      )}
    </div>
  );
}
